import * as React from "react"
import { DragIndicator } from "@mui/icons-material"
import { Box } from "@mui/material"

function ResizablePanelGroup({
  className,
  sx,
  direction = "horizontal",
  ...props
}) {
  return (
    <Box
        data-slot="resizable-panel-group"
        sx={{
            display: 'flex',
            height: '100%',
            width: '100%',
            flexDirection: direction === "vertical" ? 'column' : 'row',
            ...sx
        }}
        {...props}
    />
  )
}

function ResizablePanel({
  children,
  sx,
  defaultSize = 50,
  ...props
}) {
  return (
    <Box 
        data-slot="resizable-panel" 
        sx={{
            flex: defaultSize,
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column',
            ...sx
        }}
        {...props}
    >
        {children}
    </Box>
  )
}

function ResizableHandle({
  withHandle,
  sx,
  direction = "horizontal",
  ...props
}) {
  const handleRef = React.useRef(null);

  React.useEffect(() => {
    const handle = handleRef.current;
    if (!handle) return;

    let isDragging = false;
    let startX, startY;
    let prevSibling, nextSibling;
    let prevFlex, nextFlex;
    let totalFlex;

    const onMouseDown = (e) => {
      isDragging = true;
      startX = e.clientX;
      startY = e.clientY;
      prevSibling = handle.previousElementSibling;
      nextSibling = handle.nextElementSibling;
      
      if (prevSibling && nextSibling) {
        prevFlex = parseFloat(window.getComputedStyle(prevSibling).flexGrow) || 50;
        nextFlex = parseFloat(window.getComputedStyle(nextSibling).flexGrow) || 50;
        totalFlex = prevFlex + nextFlex;
      }
      
      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
      document.body.style.cursor = direction === "horizontal" ? 'col-resize' : 'row-resize';
      document.body.style.userSelect = 'none';
    };

    const onMouseMove = (e) => {
      if (!isDragging || !prevSibling || !nextSibling) return;
      
      if (direction === "horizontal") {
        const dx = e.clientX - startX;
        const parentWidth = handle.parentElement.getBoundingClientRect().width;
        const deltaFlex = (dx / parentWidth) * totalFlex;
        
        let newPrevFlex = prevFlex + deltaFlex;
        let newNextFlex = nextFlex - deltaFlex;
        
        if (newPrevFlex < (totalFlex * 0.1)) {
           newPrevFlex = (totalFlex * 0.1);
           newNextFlex = totalFlex - newPrevFlex;
        }
        if (newNextFlex < (totalFlex * 0.1)) {
           newNextFlex = (totalFlex * 0.1);
           newPrevFlex = totalFlex - newNextFlex;
        }
        
        prevSibling.style.flex = `${newPrevFlex} ${newPrevFlex} 0px`;
        nextSibling.style.flex = `${newNextFlex} ${newNextFlex} 0px`;
      } else {
        const dy = e.clientY - startY;
        const parentHeight = handle.parentElement.getBoundingClientRect().height;
        const deltaFlex = (dy / parentHeight) * totalFlex;
        
        let newPrevFlex = prevFlex + deltaFlex;
        let newNextFlex = nextFlex - deltaFlex;
        
        if (newPrevFlex < (totalFlex * 0.1)) {
           newPrevFlex = (totalFlex * 0.1);
           newNextFlex = totalFlex - newPrevFlex;
        }
        if (newNextFlex < (totalFlex * 0.1)) {
           newNextFlex = (totalFlex * 0.1);
           newPrevFlex = totalFlex - newNextFlex;
        }
        
        prevSibling.style.flex = `${newPrevFlex} ${newPrevFlex} 0px`;
        nextSibling.style.flex = `${newNextFlex} ${newNextFlex} 0px`;
      }
    };

    const onMouseUp = () => {
      isDragging = false;
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    handle.addEventListener('mousedown', onMouseDown);
    return () => {
      handle.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [direction]);

  return (
    <Box
      ref={handleRef}
      data-slot="resizable-handle"
      sx={{
        bgcolor: 'divider',
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: direction === "horizontal" ? 'col-resize' : 'row-resize',
        '&[data-panel-group-direction=horizontal]': {
            width: '2px',
            '&::after': {
                content: '""',
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: '50%',
                width: '4px',
                transform: 'translateX(-50%)'
            }
        },
        '&[data-panel-group-direction=vertical]': {
            height: '2px',
            width: '100%',
            '&::after': {
                content: '""',
                position: 'absolute',
                left: 0,
                right: 0,
                top: '50%',
                height: '4px',
                transform: 'translateY(-50%)'
            }
        },
        ...sx
      }}
      {...props}
    >
      {withHandle && (
        <Box 
            sx={{ 
                bgcolor: 'background.paper', 
                zIndex: 10, 
                display: 'flex', 
                height: 24, 
                width: 12, 
                alignItems: 'center', 
                justifyContent: 'center', 
                borderRadius: 0.5, 
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: 1
            }}
        >
          <DragIndicator sx={{ fontSize: 14, color: 'text.secondary' }} />
        </Box>
      )}
    </Box>
  )
}

export { ResizablePanelGroup, ResizablePanel, ResizableHandle }
